/**
 * TonkAuditPanel — recent settings changes with old → new diffs so an
 * operator can trace who tuned what and why.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const fmtValue = (v) => {
    if (v === null || v === undefined) return '—';
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'boolean') return v ? 'ON' : 'OFF';
    return String(v);
};

const TonkAuditPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/tonk/admin/audit?limit=100`, { withCredentials: true });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load audit log');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-3" data-testid="tonk-audit-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#00FFFF]" />
                    <div>
                        <p className="text-lg font-black text-white">Tonk settings audit</p>
                        <p className="text-xs text-white/50">Every rule change, admin, and reason</p>
                    </div>
                </div>
                <Button variant="ghost" onClick={load} data-testid="tonk-audit-refresh">
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0e0e0e] overflow-hidden">
                {loading ? (
                    <div className="py-16 flex justify-center"><div className="spinner" /></div>
                ) : rows.length === 0 ? (
                    <div className="py-10 px-4 text-center text-white/50 text-sm">
                        No settings changes yet.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-white/5">
                            <tr className="text-left text-[10px] uppercase tracking-widest text-white/50">
                                <th className="px-3 py-2">When</th>
                                <th className="px-3 py-2">Admin</th>
                                <th className="px-3 py-2">Field</th>
                                <th className="px-3 py-2">Old → New</th>
                                <th className="px-3 py-2">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr
                                    key={`${r.at}-${r.field}-${i}`}
                                    className="border-t border-white/5 hover:bg-white/[0.02]"
                                    data-testid={`tonk-audit-row-${i}`}
                                >
                                    <td className="px-3 py-2 text-white/70 text-[11px] whitespace-nowrap">{fmtDate(r.at)}</td>
                                    <td className="px-3 py-2 text-white/80 text-[12px]">{r.admin_name || r.admin_id}</td>
                                    <td className="px-3 py-2 text-white font-mono text-[12px]">{r.field}</td>
                                    <td className="px-3 py-2 text-[12px]">
                                        <span className="text-white/50 line-through">{fmtValue(r.old_value)}</span>
                                        <span className="text-white/40 mx-1">→</span>
                                        <span className="text-[#39FF14] font-bold">{fmtValue(r.new_value)}</span>
                                    </td>
                                    <td className="px-3 py-2 text-white/60 text-[12px] italic">{r.reason || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default TonkAuditPanel;
